import React, { type ReactNode, useCallback, memo, useRef } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Lozenge from '@atlaskit/lozenge';
import { Text } from '@atlaskit/primitives';
import { ff } from '@atlassian/jira-feature-flagging';
import { toFlagId, useFlagsService, type FlagId } from '@atlassian/jira-flags';
import HelpPanelLink from '@atlassian/jira-help-panel-button';
import JiraBoard from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/software/components/jira-board';
import JsmCalendar from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/software/components/jsm-calendar';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 } from '@atlassian/jira-intl/src/v2/types.tsx';
import { useJiraServiceManagementProjectNavigation } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/common/project-sidebar-navigation-resource/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	JSM_BOARD,
	JSM_CALENDAR,
	ENABLED,
} from '@atlassian/jira-servicedesk-common/src/service-project-features/constants.tsx';
import { usePathParam, useRouterActions } from '@atlassian/react-resource-router';
import { ExternalLink } from '../../../../../common/ui/external-link';
import { FeatureToggleTile } from '../../../../../common/ui/feature-toggle-tile/index.tsx';
import useSaveFailedFlag from '../../../../../controllers/common/save-failed-flag/index.tsx';
import useServiceProjectFeaturesController from '../../../../../controllers/common/service-project-features-controller/index.tsx';
import { useCreateSharedObjectsPermission } from '../../../../../controllers/common/shared-object-permission';
import messages from './messages';

const getLaunchUrl = (projectKey: string, boardId: number | undefined, feature: string) => {
	switch (feature) {
		case JSM_CALENDAR:
			return `/jira/servicedesk/projects/${projectKey}/boards/${boardId}/calendar`;
		case JSM_BOARD:
			return `/jira/servicedesk/projects/${projectKey}/boards/${boardId}`;
		default:
			return `/jira/servicedesk/projects/${projectKey}`;
	}
};

type SucccessFlagMessages = {
	[key: string]: {
		enabled: {
			title: MessageDescriptorV2;
			description?: MessageDescriptorV2;
			actionDescription: MessageDescriptorV2;
		};
		disabled: {
			title: MessageDescriptorV2;
			description: MessageDescriptorV2;
			actionDescription: MessageDescriptorV2;
		};
	};
};

const successFlagMessages = (feature: string, enabled: boolean) => {
	const flagMessages: SucccessFlagMessages = {
		[JSM_CALENDAR]: {
			enabled: {
				title: messages.calendarFlagSuccessTitle,
				description: messages.calendarEnabledSuccessFlagDescription,
				actionDescription: messages.calendarFeatureSuccessEnableAction,
			},
			disabled: {
				title: messages.calendarFlagSuccessTitle,
				description: messages.calendarDisabledSuccessFlagDescription,
				actionDescription: messages.calendarFeatureSuccessDisableDismiss,
			},
		},
		[JSM_BOARD]: {
			enabled: {
				title: messages.boardFlagSuccessTitle,
				actionDescription: messages.boardFeatureSuccessEnableAction,
			},
			disabled: {
				title: messages.boardFlagSuccessTitle,
				description: messages.boardDisabledSuccessFlagDescription,
				actionDescription: messages.boardFeatureSuccessDisableDismiss,
			},
		},
	};

	return flagMessages[feature][enabled ? 'enabled' : 'disabled'];
};

type ErrorFlagMessages = {
	[key: string]: {
		errorTitle: MessageDescriptorV2;
		errorDescription: MessageDescriptorV2;
	};
};

const errorFlagMessages = (feature: string) => {
	const errorMessages: ErrorFlagMessages = {
		[JSM_CALENDAR]: {
			errorTitle: messages.enabledErrorTitleForCalendar,
			errorDescription: messages.saveErrorDescription,
		},
		[JSM_BOARD]: {
			errorTitle: messages.enabledErrorTitleForBoard,
			errorDescription: messages.saveErrorDescription,
		},
	};

	return errorMessages[feature];
};

const ViewsFeatures = memo(() => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data, saving, update } = useServiceProjectFeaturesController();
	const showSaveErrorFlag = useSaveFailedFlag();
	const { hasSharedObjectsPermission } = useCreateSharedObjectsPermission();

	const { showFlag, dismissFlag } = useFlagsService();
	const { push } = useRouterActions();
	const flagIdRef = useRef<{ [key: string]: FlagId }>({});

	const [projectKey = ''] = usePathParam('projectKey');
	const { data: navData } = useJiraServiceManagementProjectNavigation();

	const onToggleSuccess = useCallback(
		(feature: string, enabled: boolean) => {
			const flagIdKey = `${feature}-service-feature-success`;
			const { current: flagIds } = flagIdRef;

			// toFlagId creates a new string reference. But we want to reuse
			// flag ids so the most relevant flag for a feature is shown
			flagIds[flagIdKey] = flagIds[flagIdKey] || toFlagId(flagIdKey);

			const id = flagIds[flagIdKey];
			const boardId = navData?.boardInfo.board.id;
			const onDismiss = () => {
				dismissFlag(id);
			};

			// Dismiss previous flag with the same id. So user cannot
			// interact with out of update flags
			dismissFlag(id);

			const flagMessages = successFlagMessages(feature, enabled);
			const launchUrl = getLaunchUrl(projectKey, boardId, feature);

			showFlag({
				id,
				type: 'success',
				title: [flagMessages.title, { value: enabled }],
				description: flagMessages?.description,
				actions: [
					{
						content: flagMessages.actionDescription,
						onClick: () => {
							onDismiss();
							if (enabled) {
								push(launchUrl);
							}
						},
					},
				],
			});
		},
		[dismissFlag, navData, projectKey, push, showFlag],
	);

	const createOnChangeHandler = useCallback(
		(featureTitle: string) =>
			(feature: string, enabled: boolean, analyticEvent: UIAnalyticsEvent) => {
				fireUIAnalytics(analyticEvent, 'settingsToggle', { field: feature });

				const errorMessages = errorFlagMessages(feature);

				update({
					feature,
					enabled,
					onFail: () => {
						showSaveErrorFlag(featureTitle, enabled, errorMessages);
					},
					onSuccess: () => onToggleSuccess(feature, enabled),
				});
			},
		[update, showSaveErrorFlag, onToggleSuccess],
	);

	const createLearnMoreLink = (
		href: string,
		message: string,
		articleId: string,
		eventName: string,
	) => {
		const learnMoreLinkButton = <ExternalLink href={href}>{message}</ExternalLink>;
		return (
			<HelpPanelLink
				articleId={articleId}
				appearance="link"
				spacing="none"
				fallbackComponent={learnMoreLinkButton}
				onClick={() =>
					fireUIAnalytics(createAnalyticsEvent({}), 'helpLink clicked', eventName, {
						articleId,
					})
				}
			>
				{message}
			</HelpPanelLink>
		);
	};

	const learnMoreAboutBoards = createLearnMoreLink(
		'https://support.atlassian.com/jira-service-management-cloud/docs/manage-your-work-using-a-board/',
		formatMessage(messages.boardLink),
		'103YhAB4GHpQvY8MAc0Q2r',
		'learnMoreAboutJsmBoards',
	);

	const learnMoreAboutCalendar = createLearnMoreLink(
		'https://support.atlassian.com/jira-service-management-cloud/docs/show-issues-in-the-calendar/',
		formatMessage(messages.calendarLink),
		'64Rt47cO6Aw6VnWaqUkriU',
		'learnMoreAboutCalendar',
	);

	const dontHaveSharedObjectsPermission =
		ff('jsm-disable-boards-features-when-no-permission_2vg02') && !hasSharedObjectsPermission;
	const disabledBoardFeatureToggle = ff('fix-jsm-board-creation-permission-issue_9bduk')
		? !data?.[JSM_BOARD]?.editable
		: dontHaveSharedObjectsPermission;

	return (
		<>
			<FeatureToggleTile
				feature={JSM_BOARD}
				illustration={<JiraBoard alt="" />}
				title={formatMessage(messages.boardTitle)}
				badge={
					ff('jsm-kanban-board-lozenge_31x91') && (
						<Lozenge appearance="new">{formatMessage(messages.newLozenge)}</Lozenge>
					)
				}
				value={data?.[JSM_BOARD]?.status === ENABLED}
				saving={saving[JSM_BOARD]}
				disabled={disabledBoardFeatureToggle}
				onChange={createOnChangeHandler(formatMessage(messages.boardTitle))}
				tooltip={
					disabledBoardFeatureToggle && ff('fix-jsm-board-creation-permission-issue_9bduk') ? (
						<FormattedMessage
							{...messages.noPermissionTooltipMessage}
							values={{
								strong: (chunks: ReactNode) => <Text as="strong">{chunks}</Text>,
							}}
						/>
					) : null
				}
			>
				{formatMessage(messages.boardDescription, {
					learnMoreLink: learnMoreAboutBoards,
				})}
			</FeatureToggleTile>
			{ff('jsm-calendar-ga_81o9m') && (
				<FeatureToggleTile
					feature={JSM_CALENDAR}
					illustration={<JsmCalendar alt="" />}
					title={formatMessage(messages.calendarTitle)}
					badge={
						ff('jsm-calendar-ga-lozenge_d5l0a') && (
							<Lozenge appearance="new">{formatMessage(messages.newLozenge)}</Lozenge>
						)
					}
					value={data?.[JSM_CALENDAR]?.status === ENABLED}
					saving={saving[JSM_CALENDAR]}
					onChange={createOnChangeHandler(formatMessage(messages.calendarTitle))}
					tooltip={
						data?.[JSM_CALENDAR]?.editable ? null : (
							<FormattedMessage
								{...messages.noPermissionTooltipMessage}
								values={{
									strong: (chunks: ReactNode) => <Text as="strong">{chunks}</Text>,
								}}
							/>
						)
					}
					disabled={!data?.[JSM_CALENDAR]?.editable}
				>
					{formatMessage(messages.calendarDescription, {
						learnMoreLink: learnMoreAboutCalendar,
					})}
				</FeatureToggleTile>
			)}
		</>
	);
});

export default ViewsFeatures;
